import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getMyProfile } from "@/lib/user.functions";
import { getMyRoles } from "@/lib/admin.functions";
import { supabase } from "@/integrations/supabase/client";

export type PlanTier = "free" | "pro" | "elite";

function useHasSession() {
  const [hasSession, setHasSession] = useState<boolean>(false);
  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (mounted) setHasSession(!!data.session);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setHasSession(!!session);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);
  return hasSession;
}

export function useMyProfile() {
  const fetchProfile = useServerFn(getMyProfile);
  const hasSession = useHasSession();
  return useQuery({
    queryKey: ["my-profile"],
    queryFn: () => fetchProfile(),
    staleTime: 30_000,
    enabled: hasSession,
  });
}

export function useIsAdmin() {
  const fetchRoles = useServerFn(getMyRoles);
  const hasSession = useHasSession();
  const { data } = useQuery({
    queryKey: ["my-roles"],
    queryFn: () => fetchRoles(),
    staleTime: 60_000,
    enabled: hasSession,
  });
  return data?.roles.includes("admin") ?? false;
}

/** Effective plan — admins always get elite-tier access regardless of billing. */
export function useEffectivePlan(): PlanTier {
  const { data } = useMyProfile();
  const isAdmin = useIsAdmin();
  if (isAdmin) return "elite";
  return (data?.profile?.plan ?? "free") as PlanTier;
}

export const FREE_PAIRS = ["EURUSD", "GBPUSD", "XAUUSD"] as const;

export const PRO_PAIRS = [
  ...FREE_PAIRS,
  "USDJPY", "AUDUSD", "USDCAD", "USDCHF", "NZDUSD",
  "GBPJPY", "EURJPY", "WTIUSD",
] as const;

export function isPairUnlocked(pair: string, plan: PlanTier | undefined | null): boolean {
  if (plan === "elite") return true;
  if (plan === "pro") return (PRO_PAIRS as readonly string[]).includes(pair);
  return (FREE_PAIRS as readonly string[]).includes(pair);
}

export function planLabel(plan: PlanTier | undefined | null): string {
  if (plan === "elite") return "Elite";
  if (plan === "pro") return "Pro";
  return "Free";
}
